import { useCallback, useMemo, useState } from 'react';

import type { FileDiffData } from '@/components/diff-viewer/diff-viewer';
import { DiffViewer } from '@/components/diff-viewer/diff-viewer';

import type { InlineCommentData } from './inline-comment';
import { InlineComment } from './inline-comment';
import { InlineCommentForm } from './inline-comment-form';

export interface DiffWithCommentsProps {
  /** Array of file diffs to display */
  files: FileDiffData[];
  /** View mode: unified or split */
  viewMode: 'unified' | 'split';
  /** Whether the diff data is currently loading */
  isLoading?: boolean;
  /** Existing inline comments to display */
  comments: InlineCommentData[];
  /** Called when a new comment is submitted */
  onSubmitComment: (params: {
    filePath: string;
    lineNumber: number;
    side: 'LEFT' | 'RIGHT';
    body: string;
  }) => Promise<void>;
  /** Callback when a file header is clicked */
  onFileSelect?: (filePath: string) => void;
}

interface ActiveCommentForm {
  filePath: string;
  lineNumber: number;
  side: 'LEFT' | 'RIGHT';
}

/**
 * Wraps the DiffViewer with inline comment support.
 * Handles:
 * - Gutter click to open comment form
 * - Rendering existing comments at their file/line/side positions
 * - Comment form submission and cancellation
 */
export function DiffWithComments({
  files,
  viewMode,
  isLoading,
  comments,
  onSubmitComment,
  onFileSelect,
}: DiffWithCommentsProps) {
  const [activeForm, setActiveForm] = useState<ActiveCommentForm | null>(null);

  // Group comments by file path for efficient lookup
  const commentsByFile = useMemo(() => {
    const map = new Map<string, InlineCommentData[]>();
    for (const comment of comments) {
      const existing = map.get(comment.filePath) ?? [];
      existing.push(comment);
      map.set(comment.filePath, existing);
    }
    return map;
  }, [comments]);

  const handleGutterClick = useCallback(
    (filePath: string, lineNumber: number, side: 'LEFT' | 'RIGHT') => {
      setActiveForm({ filePath, lineNumber, side });
    },
    [],
  );

  const handleCancel = useCallback(() => {
    setActiveForm(null);
  }, []);

  const handleSubmit = useCallback(
    async (params: {
      filePath: string;
      lineNumber: number;
      side: 'LEFT' | 'RIGHT';
      body: string;
    }) => {
      await onSubmitComment(params);
      setActiveForm(null);
    },
    [onSubmitComment],
  );

  if (isLoading) {
    return (
      <DiffViewer
        files={[]}
        viewMode={viewMode}
        isLoading={true}
      />
    );
  }

  return (
    <div className="diff-with-comments space-y-4" data-testid="diff-with-comments">
      {files.map((file) => {
        const fileComments = commentsByFile.get(file.filePath) ?? [];

        return (
          <div key={file.filePath}>
            <DiffViewer
              files={[file]}
              viewMode={viewMode}
              onGutterClick={handleGutterClick}
              onFileSelect={onFileSelect}
            />

            {/* Active comment form for this file */}
            {activeForm && activeForm.filePath === file.filePath && (
              <InlineCommentForm
                filePath={activeForm.filePath}
                lineNumber={activeForm.lineNumber}
                side={activeForm.side}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            )}

            {/* Existing comments for this file */}
            {fileComments.length > 0 && (
              <div data-testid={`comments-for-${file.filePath}`}>
                {fileComments.map((comment) => (
                  <InlineComment key={comment.id} comment={comment} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
